import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginRequest, clearError } from '../../redux/actions/authActions';
import { FileTextOutlined, ShopOutlined, BarChartOutlined, LockOutlined } from '@ant-design/icons';
import './Auth.css';

function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/purchase-order');
    }
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [isAuthenticated, navigate, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when user starts typing
    if (error) {
      dispatch(clearError());
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      dispatch(loginRequest({ ...formData, rememberMe }));
    } else {
      setValidationErrors(newErrors);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="modern-auth-container">
      {/* Left Side - Branding */}
      <div className="auth-brand-section">
        <div className="brand-content">
          <div className="brand-logo" onClick={() => navigate('/')}>
            <h1>Kalapi Prints</h1>
            <p className="brand-subtitle">Purchase Order Management</p>
          </div>

          <div className="brand-features">
            <div className="brand-feature-item">
              <div className="feature-icon-circle"><FileTextOutlined /></div>
              <div>
                <h3>Manage Orders</h3>
                <p>Create and track purchase orders efficiently</p>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon-circle"><ShopOutlined /></div>
              <div>
                <h3>Vendor Control</h3>
                <p>Maintain comprehensive vendor database</p>
              </div>
            </div>

            <div className="brand-feature-item">
              <div className="feature-icon-circle"><BarChartOutlined /></div>
              <div>
                <h3>Track Activity</h3>
                <p>Monitor all system activities with audit logs</p>
              </div>
            </div>
          </div>

          <div className="brand-footer">
            <p>Trusted by printing businesses worldwide</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="auth-form-section">
        <div className="auth-form-container">
          {/* Back to Home Link */}
          <button className="back-home-link" onClick={() => navigate('/')}>
            ← Back to Home
          </button>

          <div className="auth-header">
            <h2>Welcome Back</h2>
            <p className="auth-subtitle">Sign in to access your dashboard</p>
          </div>

          {error && (
            <div className="modern-error-alert">
              <span className="error-icon">⚠</span>
              <div className="error-content">
                <strong>Login Failed</strong>
                <p>{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-auth-form">
            <div className="modern-form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">✉</span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className={validationErrors.email ? 'error' : ''}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
              {validationErrors.email && (
                <span className="modern-error-message">{validationErrors.email}</span>
              )}
            </div>

            <div className="modern-form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <span className="input-icon"><LockOutlined /></span>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className={validationErrors.password ? 'error' : ''}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  tabIndex="-1"
                  disabled={loading}
                >
                  {showPassword ? '👁' : '👁‍🗨'}
                </button>
              </div>
              {validationErrors.password && (
                <span className="modern-error-message">{validationErrors.password}</span>
              )}
            </div>

            <div className="form-options">
              <label className="remember-me-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span>Remember me</span>
              </label>

              <Link to="/forgot-password" className="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              className="modern-auth-button"
              disabled={loading}
            >
              {loading ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="auth-footer-links">
            <p>
              Don't have an account?{' '}
              <Link to="/signup" className="signup-link">
                Create Account
              </Link>
            </p>
          </div>

          <div className="auth-divider">
            <span>Secure Login</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;